import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, X, User } from "lucide-react";
import { Logo } from "./Logo";
import { HOME } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
  { label: "Apparel", id: HOME.navApparel, href: "/#apparel" },
  { label: "Supplements", id: "nav-supplements", href: "/#supplements" },
  { label: "App", id: "nav-app", href: "/app", isRoute: true },
  { label: "Programs", id: HOME.navPrograms, href: "/#programs" },
  { label: "Creed", id: HOME.navCreed, href: "/#creed" },
];

const MORE_ITEMS = [
  { label: "Turn 180 Challenge", href: "/challenge", isRoute: true },
  { label: "MBS App", href: "/app", isRoute: true },
  { label: "About", href: "/#about" },
  { label: "Journal", href: "#journal" },
  { label: "Community", href: "#community" },
  { label: "Ambassadors", href: "#ambassadors" },
  { label: "Support", href: "#support" },
];

export const Header = () => {
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const isLoggedIn = user && typeof user === "object";

  return (
    <>
      <motion.header
        data-testid={HOME.header}
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-black/55 border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-10 py-4 md:py-5">
          {/* Logo */}
          <Link to="/" data-testid={HOME.logo} className="flex items-center gap-3 group">
            <Logo
              showWordmark={false}
              className="h-9 w-9 md:h-11 md:w-11 transition-transform duration-500 group-hover:rotate-[8deg]"
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-[10px] tracking-[0.42em] text-silver-300 font-mono">
                MIND. BODY. SOUL.
              </span>
              <span className="text-[9px] tracking-[0.5em] text-white/40 font-mono mt-1">
                EST. XXV
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              const cls =
                "relative px-4 py-2 text-[13px] tracking-[0.18em] uppercase text-white/70 hover:text-white transition-colors duration-300 group";
              const underline = (
                <span className="absolute left-4 right-4 bottom-1 h-px bg-silver-300 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
              );
              return item.isRoute ? (
                <Link
                  key={item.label}
                  to={item.href}
                  data-testid={item.id}
                  className={cls}
                >
                  <span>{item.label}</span>
                  {underline}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  data-testid={item.id}
                  className={cls}
                >
                  <span>{item.label}</span>
                  {underline}
                </a>
              );
            })}

            {/* More dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setMoreOpen(true)}
              onMouseLeave={() => setMoreOpen(false)}
            >
              <button
                data-testid={HOME.navMore}
                onClick={() => setMoreOpen((s) => !s)}
                className="flex items-center gap-1.5 px-4 py-2 text-[13px] tracking-[0.18em] uppercase text-white/70 hover:text-white transition-colors duration-300"
                aria-haspopup="true"
                aria-expanded={moreOpen}
              >
                More
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-300 ${
                    moreOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence>
                {moreOpen && (
                  <motion.div
                    data-testid={HOME.navMoreDropdown}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute right-0 top-full mt-3 min-w-[240px] p-2 rounded-xl backdrop-blur-2xl bg-black/80 border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
                  >
                    {MORE_ITEMS.map((it, idx) => {
                      const commonClass = `flex items-center justify-between px-4 py-2.5 text-[12px] tracking-[0.22em] uppercase rounded-md transition-all duration-300 ${
                        it.isRoute
                          ? "text-silver-100 hover:text-black hover:bg-silver-100 border border-silver-100/30"
                          : "text-white/70 hover:text-white hover:bg-white/5"
                      }`;
                      const content = (
                        <>
                          <span>{it.label}</span>
                          <span className="text-silver-400/50 font-mono text-[10px]">
                            {it.isRoute ? "→" : `0${idx + 1}`}
                          </span>
                        </>
                      );
                      return (
                        <motion.div
                          key={it.label}
                          initial={{ opacity: 0, x: -6 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                        >
                          {it.isRoute ? (
                            <Link to={it.href} className={commonClass}>
                              {content}
                            </Link>
                          ) : (
                            <a href={it.href} className={commonClass}>
                              {content}
                            </a>
                          )}
                        </motion.div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                data-testid="header-account-btn"
                className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/25 text-white/80 hover:text-white hover:border-silver-100 transition-colors"
              >
                <User className="h-3.5 w-3.5" strokeWidth={1.5} />
                <span className="text-[11px] tracking-[0.3em] uppercase">
                  {user.name?.split(" ")[0] || "Account"}
                </span>
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="header-login-btn"
                className="hidden md:inline-flex items-center px-4 py-2.5 text-[11px] tracking-[0.3em] uppercase text-white/70 hover:text-white transition-colors"
              >
                Sign in
              </Link>
            )}
            <Link
              to="/challenge#commit"
              className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-black bg-silver-100 hover:bg-white transition-all duration-500 px-5 py-3 rounded-full font-medium"
            >
              Turn 180
              <span className="w-1 h-1 rounded-full bg-black/70" />
            </Link>

            {/* Mobile hamburger */}
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden flex flex-col gap-1.5 p-2"
              aria-label="Menu"
              onClick={() => setMobileOpen(true)}
            >
              <span className="w-6 h-px bg-white/80" />
              <span className="w-4 h-px bg-white/80 ml-auto" />
              <span className="w-6 h-px bg-white/80" />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              key="mobile-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              key="mobile-drawer"
              data-testid="mobile-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 right-0 z-[80] w-[92%] max-w-[440px] bg-black border-l border-white/10 lg:hidden flex flex-col"
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <Logo showWordmark={false} className="h-9 w-9" />
                  <div className="font-mono text-[9px] tracking-[0.4em] text-silver-300">
                    MIND. BODY. SOUL.
                  </div>
                </div>
                <button
                  data-testid="mobile-menu-close"
                  onClick={() => setMobileOpen(false)}
                  className="p-2 rounded-full hover:bg-white/5 text-white/70 hover:text-white transition-colors"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" strokeWidth={1.5} />
                </button>
              </div>

              {/* Turn 180 CTA */}
              <div className="p-6 border-b border-white/10">
                <Link
                  to="/challenge#commit"
                  data-testid="mobile-turn180-cta"
                  onClick={() => setMobileOpen(false)}
                  className="relative flex items-center justify-between px-5 py-5 rounded-md bg-silver-100 text-black hover:bg-white transition-colors overflow-hidden"
                >
                  <div>
                    <div className="font-mono text-[9px] tracking-[0.42em] mb-1">
                      §180-DAY CHALLENGE
                    </div>
                    <div className="font-serif text-3xl leading-none">
                      Turn <span className="italic">180.</span>
                    </div>
                    <div className="mt-1 font-mono text-[9px] tracking-[0.32em] text-black/60">
                      SIGN THE COVENANT →
                    </div>
                  </div>
                  <span className="w-10 h-10 rounded-full bg-black/10 flex items-center justify-center">
                    <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                      <path d="M1 6H11M11 6L6 1M11 6L6 11" stroke="currentColor" strokeWidth="1.4" />
                    </svg>
                  </span>
                </Link>
              </div>

              {/* Primary nav */}
              <nav className="flex-1 overflow-y-auto p-4 space-y-1">
                {NAV_ITEMS.map((item, i) => {
                  const cls =
                    "group flex items-center justify-between px-4 py-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors";
                  const content = (
                    <>
                      <span className="font-serif text-white text-2xl leading-none">
                        {item.label}
                      </span>
                      <span className="font-mono text-[9px] tracking-[0.32em] text-silver-300/60">
                        0{i + 1} →
                      </span>
                    </>
                  );
                  return (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.05 }}
                    >
                      {item.isRoute ? (
                        <Link
                          to={item.href}
                          data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                          onClick={() => setMobileOpen(false)}
                          className={cls}
                        >
                          {content}
                        </Link>
                      ) : (
                        <a
                          href={item.href}
                          data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                          onClick={() => setMobileOpen(false)}
                          className={cls}
                        >
                          {content}
                        </a>
                      )}
                    </motion.div>
                  );
                })}

                {/* MORE section */}
                <div className="pt-6 mt-2">
                  <div className="px-4 font-mono text-[9px] tracking-[0.42em] text-silver-300 mb-2">
                    §MORE
                  </div>
                  {MORE_ITEMS.map((item, i) => (
                    <motion.a
                      key={item.label}
                      href={item.href}
                      onClick={() => setMobileOpen(false)}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.35 + i * 0.04 }}
                      className={`flex items-center justify-between px-4 py-3 rounded-md transition-colors ${
                        item.isRoute
                          ? "text-silver-100 hover:bg-silver-100/10"
                          : "text-white/70 hover:bg-white/5"
                      }`}
                    >
                      <span className="text-[13px] tracking-[0.14em] uppercase">
                        {item.label}
                      </span>
                      <span className="font-mono text-[9px] tracking-[0.28em] text-white/40">
                        {item.isRoute ? "→" : ""}
                      </span>
                    </motion.a>
                  ))}
                </div>
              </nav>

              {/* Footer: auth */}
              <div className="border-t border-white/10 p-6">
                {isLoggedIn ? (
                  <div className="flex items-center justify-between gap-3">
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="flex-1 inline-flex items-center gap-2 px-4 py-3 rounded-full border border-silver-100/40 text-silver-100 hover:text-white hover:border-silver-100"
                    >
                      <User className="h-4 w-4" strokeWidth={1.5} />
                      <span className="text-[11px] tracking-[0.28em] uppercase">
                        Dashboard · {user.name?.split(" ")[0]}
                      </span>
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setMobileOpen(false);
                        navigate("/");
                      }}
                      className="px-4 py-3 text-[11px] tracking-[0.28em] uppercase text-white/60 hover:text-white"
                    >
                      Sign out
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Link
                      to="/login"
                      onClick={() => setMobileOpen(false)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-full border border-white/20 text-white/80 hover:text-white hover:border-white/50 text-[11px] tracking-[0.28em] uppercase"
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileOpen(false)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-full bg-silver-100 text-black hover:bg-white text-[11px] tracking-[0.28em] uppercase font-medium"
                    >
                      Enlist
                    </Link>
                  </div>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
